import React, { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import BaseIcon from "../../../../components/BaseIcon";
import GridCard from "../../../../components/GridCard";
import useViewController from "./useViewController";
import BaseTextButton from "../../../../components/BaseTextButton";
import BaseDivideLine from "../../../../components/BaseDivideLine";
import Game from "../../../../store/Game";
import useModalActions from "../../../../hooks/useModalActions";
import { MODAL_TYPES } from "../../../../utils/emitter";
import "./style.scss";
import BaseButton from "../../../../components/BaseButton";
import { useNavigate } from "react-router-dom";
import useWindowSize from "../../../../hooks/useWindowSize";
import { demonstrationStore } from "../../../../store/Demonstration";
import ROUTES from "../../../../utils/routes";
import { disableRubberBandScroll } from "../../../../utils/disableRubberBand";

export default observer(function ZoneItem({
  item,
  isDemo,
  checkCard,
  indexZone,
  gameId,
}) {
  const {
    cards,
    title,
    texts,
    insight_form_title,
    insight_form_texts,
    icon_name,
    key,
  } = item;

  const navigate = useNavigate();
  const { openModal } = useModalActions();
  const { t } = useTranslation();
  const { openCard, descriptionIsOpen, showDescription, closeCard } =
    useViewController();
  const { windowSize } = useWindowSize();

  // 28.09: обучающая подсказка указывает на первую карточку зоны. Раньше зона была
  // жёстко «первая по счёту» (indexZone === 0) — если у пары там 0 карточек (у всех
  // ответов разошлись мнения, «Зона комфорта 0 из 88»), цели не было, и подсказка
  // улетала в левый верхний угол. Берём первую зону, где карточки ЕСТЬ.
  const demoZoneKey = Game.zones.find(
    (z) => z.kind !== "personal" && (z.cards || []).length > 0
  )?.key;
  const isDemoZone = key === demoZoneKey;

  const [storageDescriptionIsOpen, setStorageDescriptionIsOpen] =
    useState(false);

  useEffect(() => {
    if (
      demonstrationStore.isOpen &&
      demonstrationStore.step === 2 &&
      isDemoZone &&
      cards.length > 0
    ) {
      // 21.09: обучающая демонстрация отдавала свайперу ВЕСЬ массив зоны, минуя
      // превью-лимит, — у некупленной игры оттуда пролистывались все карточки.
      // Отдаём тот же срез, что и обычный клик по карточке.
      openCard(
        { ...cards[0], id: 0 },
        isDemo ? cards.slice(0, adaptiveCountCardsToShow) : cards
      );
    }
  }, [
    demonstrationStore.isOpen,
    demonstrationStore.step,
    isDemoZone,
    cards,
    openCard,
  ]);

  useEffect(() => {
    if (demonstrationStore.finished) {
      closeCard({ ...cards[0], id: 0 }, cards);
    }
  }, [demonstrationStore.finished]);

  useEffect(() => {
    const descriptionOpen = localStorage.getItem(`${gameId}_${key}`);
    if (descriptionOpen) setStorageDescriptionIsOpen(true);
  }, [gameId, key]);

  const descriptionWasOpen = (gameId, zoneKey) => {
    localStorage.setItem(`${gameId}_${zoneKey}`, true);
    setStorageDescriptionIsOpen(true);
  };

  const adaptiveCountCardsToShow = useMemo(() => {
    // Доработка 15.07: до оплаты игры видно только 4 первые карточки зоны на
    // мобайле и 5 в вебе (undefined width на первом рендере трактуем как веб)
    if (isDemo) {
      return windowSize.width < 623 ? 4 : 5;
    }
    if (windowSize.width < 1217 && windowSize.width > 974) {
      return 8;
    }
    if (windowSize.width < 974 && windowSize.width > 623) {
      return 9;
    }
    if (windowSize.width < 623) {
      return 4;
    }
    return 10;
  }, [windowSize.width, isDemo]);

  const handleShowMore = () => {
    // Доработка 15.07: у неоплаченной игры «Показать все карточки» не разворачивает
    // зону, а ведёт в покупку. 03.08: попап «Хочешь увидеть весь тест полностью?»
    // теперь с ДВУМЯ тарифами (тест-88 + полная игра) + блок «Обрати внимание!».
    if (isDemo) {
      openModal(MODAL_TYPES.SEE_FULL_TEST, { gameId });
      return;
    }
    const newValue = !item.isShowMore;
    Game.showMore(key, newValue);

    if (item.isShowMore) {
      const element =
        isDemo || descriptionIsOpen
          ? document.getElementById(`item-zone-button-card-${key}`)
          : document.getElementById(`item-zone-button-instruction-${key}`);

      setTimeout(() => {
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }
      }, 150);
    }
  };

  const clsCardsWrapper = ["item-zone__cards-wrapper"];
  if (cards.length <= adaptiveCountCardsToShow)
    clsCardsWrapper.push("item-zone__cards-wrapper_prem");

  // item 7: знаменатель «X из Y карт». Полная игра (PREMIUM) теперь имеет
  // hasExtendedSet=true → totalCards=145, но карты +57 попадают в зоны только когда
  // ОБА прошли +57. Пока не прошли — показываем базовые 88 (иначе «X из 145» при 88).
  const ext57InZones =
    Game.game?.ownerFinishedExtended && Game.game?.invitedFinishedExtended;
  const zoneTotal = ext57InZones ? Game.game?.totalCards ?? 88 : 88;

  return (
    <div className="item-zone" id={`zone-${key}`}>
      <div className="item-zone__header">
        <div className="item-zone__header-left">
          <div className="item-zone__icons">
            <BaseIcon name={icon_name} />
          </div>
          <div className="item-zone__title">
            <div>{t(title)}</div>
            {cards && Array.isArray(cards) && cards.length ? (
              <div>
                {t("demo_result_test_page.from_to", {
                  from: cards.length,
                  to: zoneTotal,
                })}
              </div>
            ) : (
              <div>
                {t("demo_result_test_page.from_to", {
                  from: 0,
                  to: zoneTotal,
                })}
              </div>
            )}
            {!cards.length ? (
              <div className="item-zone__header-no-cards item-zone__header-no-cards__mobile">
                {t("demo_result_test_page.no_cards")}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="item-zone__content">
        {cards && Array.isArray(cards) && cards.length ? (
          <div className={clsCardsWrapper.join(" ")}>
            <div className="item-zone__cards">
              {cards
                .slice(
                  0,
                  !item.isShowMore ? adaptiveCountCardsToShow : cards.length
                )
                .map((card, i) => {
                  return (
                    <GridCard
                      isDemonstrationActiveCard={
                        demonstrationStore.isOpen && isDemoZone && i === 0
                      }
                      selectCard={() => checkCard(card)}
                      type={!isDemo ? "select" : "default"}
                      // item 30: в демо (игра не куплена) видны только превью-карточки
                      // (adaptiveCountCardsToShow). Раньше в свайпер передавался ВЕСЬ
                      // массив cards — и пролистыванием можно было увидеть все карты.
                      // Отдаём в свайпер только превью-срез, чтобы недоступные карты
                      // нельзя было пролистать. id=i — индекс в срезе, initialSlide верный.
                      openCard={() =>
                        openCard(
                          { ...card, id: i },
                          isDemo ? cards.slice(0, adaptiveCountCardsToShow) : cards
                        )
                      }
                      card={card}
                      key={card.id}
                      id={`card-${card.id}`}
                      gameId={gameId}
                    />
                  );
                })}
            </div>
            {cards.length > adaptiveCountCardsToShow ? (
              <div
                className="item-zone__hide"
                id={`item-zone-button-card-${key}`}
              >
                <BaseTextButton
                  className="item-zone__button-card"
                  onClick={handleShowMore}
                >
                  {item.isShowMore
                    ? t("hide")
                    : t("demo_result_test_page.showAllCards")}
                </BaseTextButton>
              </div>
            ) : null}
          </div>
        ) : null}
        <div
          className={[
            "item-zone__text-content",
            // item 32: и в демо (не куплено) инструкции по умолчанию СВЁРНУТЫ за
            // кнопкой (раньше `|| isDemo` держал их всегда развёрнутыми — «километр
            // текста»). Теперь и там, и там — управляется тем же тумблером.
            descriptionIsOpen && "item-zone__text-content_opened",
            (key === "zoneInterest" ||
              key === "zoneDialog" ||
              key === "zoneContradiction") &&
              "item-zone__text-content__column",
          ].join(" ")}
        >
          <div
            className={[
              "item-zone__texts",
              isDemo ? "item-zone__texts_demo" : "",
              (key === "zoneInterest" ||
                key === "zoneDialog" ||
                key === "zoneContradiction") &&
                "item-zone__texts__column",
            ].join(" ")}
          >
            {texts.map((text, index) => {
              return (
                <div key={index} className="item-zone__text">
                  {t(text)}
                </div>
              );
            })}
          </div>
          <div className="item-zone__info-blocks-wrapper">
            <div
              className={[
                "item-zone__info-blocks",
                (key === "zoneInterest" ||
                  key === "zoneDialog" ||
                  key === "zoneContradiction") &&
                  "item-zone__info-blocks__column",
              ].join(" ")}
            >
              <div
                className={[
                  "item-zone__info-block-wrapper",
                  (key === "zoneInterest" ||
                    key === "zoneDialog" ||
                    key === "zoneContradiction") &&
                    "item-zone__info-block-wrapper__column",
                ].join(" ")}
              >
                <div
                  className={[
                    "item-zone__info-block",
                    (key === "zoneInterest" ||
                      key === "zoneDialog" ||
                      key === "zoneContradiction") &&
                      "item-zone__info-block__column",
                  ].join(" ")}
                >
                  <div className="item-zone__info-block-header">
                    {t(insight_form_title)}
                  </div>
                  <ul className="item-zone__info-block--texts">
                    {insight_form_texts.map((text, index) => {
                      return (
                        <li className="item-zone__info-block--text" key={index}>
                          <div className="item-zone__info-block--text--disc"></div>
                          {t(text)}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        {isDemo ? (
          // ТЗ п.7.2: анкета откровений — 2 кнопки (деньги/баллы) через попап покупки игры
          <div className="item-zone__button_fill-in-form">
            <BaseButton
              onClick={() =>
                openModal(MODAL_TYPES.BUY_PRODUCT, {
                  productType: "GAME",
                  variant: "anketa",
                  gameId: Game.game?.id,
                })
              }
            >
              {t("demo_result_test_page.zone_fill_in_form")}
            </BaseButton>
          </div>
        ) : null}
        {cards.length ? (
          // item 32: тумблер «Инструкция»/«Свернуть» показываем и в демо тоже,
          // чтобы длинный текст был спрятан под кнопкой (раньше только для купленной).
          <div
            className="item-zone__instruction-block-mobile"
            id={`item-zone-button-instruction-${key}`}
          >
            <BaseButton
              onClick={() => {
                showDescription(!descriptionIsOpen);
                descriptionWasOpen(gameId, key);
                if (descriptionIsOpen) {
                  setTimeout(() => {
                    const element = document.getElementById(`zone-${key}`);
                    if (element) {
                      element.scrollIntoView({
                        behavior: "smooth",
                        block: "end",
                      });
                    }
                  }, 150);
                }
              }}
              className="item-zone__instruction-button"
              gradientWrapperCls="item-zone__instruction-button"
              unfilledGradient={descriptionIsOpen || storageDescriptionIsOpen}
            >
              {!descriptionIsOpen
                ? t("demo_result_test_page.instruction")
                : t("demo_result_test_page.instruction_down")}
            </BaseButton>
          </div>
        ) : null}
      </div>
      <BaseDivideLine dash />
    </div>
  );
});
